import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, BarChart3, Users, GraduationCap, BookOpen, ClipboardCheck,
  LogOut, Calendar, FileText, Clock, Library, Bus, Bell,
  Briefcase, Heart, Trophy, UserPlus, Receipt, Image, UserCheck,
  ScrollText, MessageSquare, HelpCircle, BookOpenCheck, NotebookPen, Printer,
  Settings, ChevronsUpDown, UserCog
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../utils/accessControl';
import Logo from '../common/Logo';
import '../styles/layout.css';

const navSections = [
  { title: 'Overview', items: [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' }
  ]},
  { title: 'People', items: [
    { to: '/students', icon: Users, label: 'Students' },
    { to: '/teachers', icon: GraduationCap, label: 'Teachers' },
    { to: '/admissions', icon: UserPlus, label: 'Admissions' }
  ]},
  { title: 'Academics', items: [
    { to: '/attendance', icon: ClipboardCheck, label: 'Attendance' },
    { to: '/marks', icon: BookOpen, label: 'Marks' },
    { to: '/homework', icon: FileText, label: 'Homework' },
    { to: '/timetable', icon: Clock, label: 'Timetable' },
    { to: '/question-bank', icon: HelpCircle, label: 'Question Bank' },
    { to: '/lesson-plans', icon: BookOpenCheck, label: 'Lesson Plans' }
  ]},
  { title: 'School', items: [
    { to: '/events', icon: Calendar, label: 'Events' },
    { to: '/announcements', icon: Bell, label: 'Announcements' },
    { to: '/circulars', icon: ScrollText, label: 'Circulars' },
    { to: '/library', icon: Library, label: 'Library' },
    { to: '/transport', icon: Bus, label: 'Transport' },
    { to: '/gallery', icon: Image, label: 'Gallery' },
    { to: '/visitors', icon: UserCheck, label: 'Visitors' }
  ]},
  { title: 'Welfare', items: [
    { to: '/health', icon: Heart, label: 'Health Records' },
    { to: '/achievements', icon: Trophy, label: 'Achievements' },
    { to: '/leaves', icon: Briefcase, label: 'Leaves' },
    { to: '/student-diary', icon: NotebookPen, label: 'Diary' },
    { to: '/feedback', icon: MessageSquare, label: 'Feedback' }
  ]},
  { title: 'Staff', items: [
    { to: '/staff-attendance', icon: UserCog, label: 'Staff Attendance' }
  ]},
  { title: 'Finance', items: [
    { to: '/expenses', icon: Receipt, label: 'Expenses' }
  ]},
  { title: 'Tools', items: [
    { to: '/generators', icon: Printer, label: 'Document Generators' },
    { to: '/settings', icon: Settings, label: 'Settings' }
  ]}
];

const Sidebar = ({ isOpen }) => {
  const { school, user, logout } = useAuth();
  const role = user?.role;

  // Drop items the current role can't access, then drop sections left empty.
  const visibleSections = navSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => canAccess(role, item.to))
    }))
    .filter(section => section.items.length > 0);

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <Logo size={30} showWordmark wordmarkSize={16} id="sb" />
      </div>

      {school && (
        <div className="team-switcher" title="Active school">
          <div className="team">
            <div className="team-mark">{(school.code || school.name || 'S').charAt(0)}</div>
            <span>{school.name || school.code}</span>
          </div>
          <ChevronsUpDown size={14} color="var(--ink-4)" />
        </div>
      )}

      <nav className="sidebar-nav">
        {visibleSections.map((section) => (
          <div className="nav-section" key={section.title}>
            <div className="nav-section-title">{section.title}</div>
            <ul>
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  >
                    <item.icon size={16} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <button className="btn-logout" onClick={logout}>
          <LogOut size={16} />
          <span>Log out</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
